// src/WorkOrders.jsx
import React, { useState, useEffect, useMemo } from 'react';
import WoDetails from './WoDetails.jsx';
import moment from 'moment';
import { fetchAuthSession } from 'aws-amplify/auth';

export default function WorkOrders() {
  const [items, setItems] = useState([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [checkboxStates, setCheckboxStates] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'unitNumber', direction: 'ascending' });
  const [searchTerm, setSearchTerm] = useState('');
  const [timeframe, setTimeframe] = useState('2 weeks');
  const [isDetailsVisible, setIsDetailsVisible] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');
  const [editMode, setEditMode] = useState(false);

  const apiUrl = 'https://2af2e5kjj9.execute-api.us-east-1.amazonaws.com/prod/alltrucks?type=300';
  const updateUrl = 'https://2af2e5kjj9.execute-api.us-east-1.amazonaws.com/prod/updateWO';

  async function authHeaders() {
    const { tokens } = await fetchAuthSession();
    const jwt =
      tokens?.idToken?.toString?.() ??
      tokens?.accessToken?.toString?.();
    return jwt ? { Authorization: `Bearer ${jwt}` } : {};
  }

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchItems = async () => {
    try {
      const res = await fetch(apiUrl, {
        headers: {
          Accept: 'application/json',
          ...(await authHeaders()),
        },
      });

      const text = await res.text();
      let data;
      try { data = text ? JSON.parse(text) : []; } catch { data = []; }

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          throw new Error('Unauthorized. Please sign in to view work orders.');
        }
        throw new Error(data?.message || `GET /alltrucks?type=300 -> HTTP ${res.status}`);
      }

      const safe = Array.isArray(data) ? data : [];
      setItems(safe);

      // init check states
      const initialCheckStates = {};
      safe.forEach(it => { if (it?.id) initialCheckStates[it.id] = false; });
      setCheckboxStates(initialCheckStates);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Fetching error:', error);
      setItems([]);
      setCheckboxStates({});
    }
  };

  const sortedAndFilteredItems = useMemo(() => {
    const filtered = items.filter(item => {
      const dateStr = String(item?.dateEntered || '');
      const itemDate = moment(dateStr, 'MM/DD/YYYY', true);
      const withinTimeframe = {
        '2 weeks': itemDate.isValid() ? moment().diff(itemDate, 'weeks') <= 2 : true,
        '3 months': itemDate.isValid() ? moment().diff(itemDate, 'months') <= 3 : true,
        '6 months': itemDate.isValid() ? moment().diff(itemDate, 'months') <= 6 : true,
        'all': true,
      }[timeframe] ?? true;

      const q = searchTerm.trim().toLowerCase();
      if (!q) return withinTimeframe;

      const fields = [
        item?.unitNumber, item?.woNum, item?.description,
        item?.unitHealth, item?.Vendor, item?.status, item?.blockID,
      ];
      return withinTimeframe && fields.some(v => String(v || '').toLowerCase().includes(q));
    });

    return filtered.sort((a, b) => {
      const dirMul = sortConfig.direction === 'ascending' ? 1 : -1;
      const av = String(a?.[sortConfig.key] ?? '').toLowerCase();
      const bv = String(b?.[sortConfig.key] ?? '').toLowerCase();
      return av.localeCompare(bv, 'en', { numeric: true }) * dirMul;
    });
  }, [items, sortConfig, timeframe, searchTerm]);

  const handleCheckboxChange = (item) => {
    if (!item?.id) return;
    setCheckboxStates(prev => ({ ...prev, [item.id]: !prev[item.id] }));
  };

  const handleTimeframeChange = (e) => setTimeframe(e.target.value);

  const requestSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending',
    }));
  };

  const handleViewDetails = (item, isEditMode) => {
    setSelectedWorkOrder(item);
    setEditMode(isEditMode);
    setIsDetailsVisible(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsVisible(false);
    setSelectedWorkOrder(null);
    setSelectedAction('');
    setEditMode(false);
    fetchItems();
  };

  const handleActionChange = (event) => {
    const action = event.target.value;
    setSelectedAction(action);
    setEditMode(action === 'editDetails' || action === 'newItem');

    const selectedItem = items.find(it => checkboxStates[it?.id]);
    switch (action) {
      case 'viewDetails':
      case 'editDetails':
        if (selectedItem) handleViewDetails(selectedItem, action === 'editDetails');
        break;
      case 'markCompleted':
        markSelectedAsCompleted();
        setSelectedAction('');
        break;
      case 'newItem':
        handleViewDetails({
          id: String(Date.now()),
          unitNumber: '',
          woNum: '',
          description: '',
          blockID: '',
          unitHealth: '',
          Vendor: '',
          dateEntered: moment().format('MM/DD/YYYY'),
          dateCompleted: '',
          status: '',
          type: 300,
        }, true);
        break;
      default:
        // no-op
        break;
    }
  };

  const markSelectedAsCompleted = async () => {
    // Only update items that are selected and not yet completed
    const updates = items
      .filter(it => checkboxStates[it?.id] && (!it?.dateCompleted || it.dateCompleted === ''))
      .map(it => ({
        ...it,
        dateCompleted: moment().format('MM/DD/YYYY'),
      }));

    // Update each changed item; wait for all to settle, then refresh
    await Promise.allSettled(updates.map(updateItem));
    fetchItems();
  };

  const updateItem = async (item) => {
    try {
      const res = await fetch(updateUrl, {
        method: 'POST', // fixed case
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(await authHeaders()),
        },
        body: JSON.stringify(item),
      });

      const text = await res.text();
      let data;
      try { data = text ? JSON.parse(text) : null; } catch { data = null; }

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          throw new Error('Unauthorized. Please sign in to update work orders.');
        }
        throw new Error(data?.message || `Update failed (HTTP ${res.status}).`);
      }
      // success (many APIs return empty body; nothing else to do)
      // console.log('Update OK for item:', item.id, data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error updating item:', item?.id, err);
      throw err;
    }
  };

  const isAnySelected = Object.values(checkboxStates).some(Boolean);
  const isMarkCompletedAvailable =
    isAnySelected &&
    items.every(it => (checkboxStates[it?.id] ? (String(it?.dateCompleted || '') === '') : true));

  return (
    <div className="container listDiv">
      <div className="search-and-add-bar">
        <div>
          <label className="input-label">Timeframe</label>
          <select value={timeframe} onChange={handleTimeframeChange} className="timeframe-dropdown">
            <option value="2 weeks">2 Weeks</option>
            <option value="3 months">3 Months</option>
            <option value="6 months">6 Months</option>
            <option value="all">All</option>
          </select>
        </div>
        <div>
          <label className="input-label">Search</label>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div>
          <label className="input-label">Actions</label>
          <select
            className="action-dropdown"
            value={selectedAction}
            onChange={handleActionChange}
          >
            <option value="">Select Action</option>
            <option value="editDetails" disabled={!isAnySelected}>Edit Item</option>
            <option value="markCompleted" disabled={!isMarkCompletedAvailable}>Mark Completed</option>
            <option value="newItem">New Item</option>
            <option value="viewDetails" disabled={!isAnySelected}>View Details</option>
          </select>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th className="checkbox-header"></th>
              <th className="styleCol1" onClick={() => requestSort('unitNumber')}>Unit</th>
              <th className="styleCol1" onClick={() => requestSort('woNum')}>Work Order</th>
              <th className="styleCol1" onClick={() => requestSort('description')}>Description</th>
              <th className="styleCol1" onClick={() => requestSort('unitHealth')}>Health</th>
              <th className="styleCol1" onClick={() => requestSort('status')}>Status</th>
              <th className="styleCol1" onClick={() => requestSort('Vendor')}>Vendor</th>
              <th className="styleCol1" onClick={() => requestSort('blockID')}>Block ID</th>
              <th className="styleCol1" onClick={() => requestSort('dateEntered')}>Entered</th>
              <th className="styleCol1" onClick={() => requestSort('dateCompleted')}>Completed</th>
            </tr>
          </thead>
          <tbody className="scrollable-tbody">
            {sortedAndFilteredItems.length > 0 ? (
              sortedAndFilteredItems.map((item) => (
                <tr key={item.id}>
                  <td className="checkbox-cell">
                    <input
                      type="checkbox"
                      checked={checkboxStates[item.id] || false}
                      onChange={() => handleCheckboxChange(item)}
                    />
                  </td>
                  <td className="styleCol1">{item.unitNumber}</td>
                  <td className="styleCol1">{item.woNum}</td>
                  <td className="styleCol1">{item.description}</td>
                  <td className="styleCol1">{item.unitHealth}</td>
                  <td className="styleCol1">{item.status}</td>
                  <td className="styleCol1">{item.Vendor}</td>
                  <td className="styleCol1">{item.blockID}</td>
                  <td className="styleCol1">{item.dateEntered}</td>
                  <td className="styleCol1">{item.dateCompleted}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center' }}>No data available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isDetailsVisible && selectedWorkOrder && (
        <WoDetails
          item={selectedWorkOrder}
          onClose={handleCloseDetails}
          editMode={editMode}
          setEditMode={setEditMode}   /* pass through so the child can toggle */
        />
      )}
    </div>
  );
}
